import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Copy, AlertTriangle, Phone, Mail, Info, Check, Merge, Search } from 'lucide-react';
import { contactsService } from 'api/services/contacts.service';
import { LoadingSpinner } from 'components/Common/LoadingSpinner';
import { ErrorMessage } from 'components/Common/ErrorMessage';

/**
 * Contacts that look like the same person twice.
 *
 * Read-only on purpose. Merging is destructive in a way that is hard to undo —
 * WhatsApp messages, call events and campaign recipients all reference a contact id,
 * so a merge that does not repoint them moves the problem instead of solving it.
 * This shows the scale and lets somebody open each record and judge.
 */
const ContactDuplicates: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Which record survives, per group. Defaults to the oldest — the one other rows
  // are most likely already pointing at — but the operator decides, because no global
  // policy is right for every pair.
  const [keepers, setKeepers] = useState<Record<string, string>>({});
  const [merging, setMerging] = useState<string | null>(null);
  const [done, setDone] = useState<Record<string, string>>({});
  const [bulk, setBulk] = useState<{ total: number; complete: number } | null>(null);
  // The server-side sweep, which is a different thing from mergeAll: that one
  // merges what is SHOWN, this one merges every group there is.
  const [sweep, setSweep] = useState<{ merged: number; remaining: number } | null>(null);
  const [sweepPlan, setSweepPlan] = useState<{ wouldMerge: number; groups: number } | null>(null);

  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');

  const load = async (term = query) => {
    const r = await contactsService.getDuplicates(200, term || undefined);
    if (r.success) setData(r.data); else setError(r.error || 'Could not load duplicates');
  };

  const runSearch = async () => {
    setQuery(search);
    setLoading(true);
    await load(search);
    setLoading(false);
  };

  useEffect(() => {
    (async () => { await load(); setLoading(false); })();
  }, []);

  /**
   * Ask the server what a full sweep would do, and change nothing.
   *
   * Separate from running it because the count is the thing worth seeing first:
   * 3,009 groups is not a number anybody should discover after pressing a
   * button. The server previews unless told to apply.
   */
  const previewSweep = async () => {
    setError(null);
    const r = await contactsService.mergeDuplicatesByPhone(500, false);
    if (!r.success) { setError(r.error || 'Could not preview'); return; }
    setSweepPlan({ wouldMerge: r.data?.wouldMerge || 0, groups: r.data?.groupsWithDuplicates || 0 });
  };

  /**
   * Merge every duplicated phone number, in server-side batches.
   *
   * Loops on groupsRemaining rather than a batch count the browser guesses, so
   * it finishes whatever the size and cannot half-run because a page only had
   * 200 groups loaded. Each batch is one request; the per-pair loop that
   * "Merge all shown" uses would be 3,009 of them.
   */
  const runSweep = async () => {
    const total = sweepPlan?.groups || 0;
    if (!window.confirm(
      `Merge every contact that shares a phone number?\n\n` +
      `${total} group${total === 1 ? '' : 's'} across the whole organisation — not just the ones on screen.\n` +
      `The oldest record in each is kept. The others are archived, not deleted.`)) return;

    setError(null);
    setSweep({ merged: 0, remaining: total });
    let merged = 0;
    // Bounded rather than while(true): a server that keeps reporting work left
    // would otherwise spin here forever.
    for (let pass = 0; pass < 100; pass++) {
      const r = await contactsService.mergeDuplicatesByPhone(500, true);
      if (!r.success) { setError(r.error || 'Merge failed'); break; }
      merged += r.data?.merged || 0;
      const remaining = r.data?.groupsRemaining ?? 0;
      setSweep({ merged, remaining });
      if (!r.data?.runAgain) break;
    }
    setSweep(null);
    setSweepPlan(null);
    setLoading(true);
    await load();
    setLoading(false);
  };

  const groupKey = (g: any, i: number) => `${g.matchedOn}-${g.value}-${i}`;

  /**
   * Merge every group currently on screen, keeping each group's selected survivor.
   *
   * <p>Scoped to what is SHOWN, not to all 618. Merging groups the operator has not
   * looked at — and cannot see — would be acting on their behalf at a scale they did
   * not choose. With a search applied this merges the matches; without one, the
   * loaded page.
   *
   * <p>Stops at the first failure rather than pressing on. If something is wrong with
   * the data or the connection, the second hundred merges will be wrong the same way,
   * and a partial run the operator can see the end of is far easier to reason about.
   */
  const mergeAll = async () => {
    const pending = groups
      .map((g: any, i: number) => ({ g, i, key: groupKey(g, i) }))
      .filter(({ g, key }) => !done[key] && (g.contacts || []).length > 1);
    if (pending.length === 0) return;

    const records = pending.reduce((n, { g }) => n + ((g.contacts || []).length - 1), 0);
    if (!window.confirm(
      `Merge ${pending.length} group${pending.length === 1 ? '' : 's'} shown here?\n\n` +
      `${records} record${records === 1 ? '' : 's'} will be folded into the one marked "keep this" ` +
      `in each group and archived. Their messages, calls, campaign history and remarks move across.\n\n` +
      `This only covers what is on screen${query ? ' for this search' : ''} — not all ` +
      `${totalGroups} groups.`)) return;

    setBulk({ total: pending.length, complete: 0 });
    setError(null);
    let completed = 0;

    for (const { g, key } of pending) {
      const survivorId = keepers[key] || g.contacts?.[0]?.id;
      const others: any[] = (g.contacts || []).filter((c: any) => c.id !== survivorId);
      let moved = 0;
      for (const o of others) {
        const r = await contactsService.mergeContacts(survivorId, o.id);
        if (!r.success) {
          setError(`Stopped after ${completed} of ${pending.length}: ${r.error || 'merge failed'}`);
          setBulk(null);
          await load();
          return;
        }
        moved += Object.values(r.data?.moved || {}).reduce((a: number, b: any) => a + (Number(b) || 0), 0);
      }
      completed++;
      setDone((d) => ({ ...d, [key]: `Merged — ${moved} record${moved === 1 ? '' : 's'} moved across.` }));
      setBulk({ total: pending.length, complete: completed });
    }

    setBulk(null);
    await load();
  };

  const mergeGroup = async (g: any, i: number) => {
    const key = groupKey(g, i);
    const survivorId = keepers[key] || g.contacts?.[0]?.id;
    const others: any[] = (g.contacts || []).filter((c: any) => c.id !== survivorId);
    if (!survivorId || others.length === 0) return;

    const keptName = (g.contacts || []).find((c: any) => c.id === survivorId)?.name || survivorId;
    // Confirmed explicitly, naming which record is kept. This moves history between
    // records and there is no undo in the UI — only in the data.
    if (!window.confirm(
      `Keep "${keptName}" and fold ${others.length} other record${others.length === 1 ? '' : 's'} into it?\n\n` +
      `Their messages, calls, campaign history and remarks move across. The other ` +
      `record${others.length === 1 ? ' is' : 's are'} archived, not deleted.`)) return;

    setMerging(key);
    setError(null);
    let movedTotal = 0;
    for (const o of others) {
      const r = await contactsService.mergeContacts(survivorId, o.id);
      if (!r.success) {
        setError(r.error || 'Merge failed');
        setMerging(null);
        return;
      }
      movedTotal += Object.values(r.data?.moved || {}).reduce((a: number, b: any) => a + (Number(b) || 0), 0);
    }
    setDone((d) => ({ ...d, [key]: `Merged — ${movedTotal} record${movedTotal === 1 ? '' : 's'} moved across.` }));
    setMerging(null);
    await load();
  };

  if (loading) return <LoadingSpinner />;
  if (error) return <div className="p-6"><ErrorMessage message={error} /></div>;

  const groups: any[] = data?.groups || [];
  const totalGroups = (data?.phoneGroups || 0) + (data?.emailGroups || 0);
  const involved = data?.contactsInvolved || 0;

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      <button onClick={() => navigate('/user-directory/contacts')}
        className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300 hover:text-indigo-600">
        <ArrowLeft className="w-4 h-4" /> Contacts
      </button>

      <div>
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
          <Copy className="w-5 h-5" /> Possible duplicates
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Contacts in this organisation sharing a phone number or an email address.
        </p>
      </div>

      {/* 618 groups against a 200 cap means a given person lands wherever they land —
          the first one anybody wanted to check sat at position 567. Without this the
          list can only be worked top-down. */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[16rem]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') runSearch(); }}
            placeholder="Find a number or address — searches all groups, not just the ones shown"
            className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
          />
        </div>
        <button onClick={runSearch}
          className="tl-btn tl-btn-primary tl-btn-sm">Search</button>
        {query && (
          <button onClick={() => { setSearch(''); setQuery(''); setLoading(true); load('').then(() => setLoading(false)); }}
            className="px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300">
            Clear
          </button>
        )}
      </div>

      {query && (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Showing groups matching “{query}”. The counts below describe the matches, not the whole organisation.
        </p>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat label="Groups" value={totalGroups} />
        {/* The number that sizes the work. Three contacts on one number is ONE group
            and two people's worth of cleanup. */}
        <Stat label="Contacts involved" value={involved} tone="text-amber-600" />
        <Stat label="By phone" value={data?.phoneGroups || 0} />
        <Stat label="By email" value={data?.emailGroups || 0} />
      </div>

      {data?.truncated && (
        <div className="flex items-start gap-2 text-xs rounded-lg px-3 py-2 bg-amber-50 text-amber-800 border border-amber-200
                        dark:bg-amber-900/20 dark:text-amber-200 dark:border-amber-800">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          <span>
            Showing the first {data.shown} of {totalGroups} groups. The counts above are the real
            totals — the list is capped, not the problem.
          </span>
        </div>
      )}

      <div className="flex items-start gap-2 text-xs rounded-lg px-3 py-2 bg-blue-50 text-blue-900 border border-blue-200
                      dark:bg-blue-900/20 dark:text-blue-200 dark:border-blue-800">
        <Info className="w-4 h-4 shrink-0 mt-0.5" />
        <span>
          Choose which record to keep, then merge. Messages, calls, campaign history and remarks
          all move across to it. The other records are <b>archived, not deleted</b> — they keep a
          pointer to where they went, so a mistake can be undone in the data. Blank fields on the
          record you keep are filled from the others; nothing already set is overwritten.
        </span>
      </div>

      {groups.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-gray-100 dark:border-gray-700 pt-4">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {/* Says exactly what the button covers. "Merge all" over a capped list is
                otherwise read as "all 618", which it is not. */}
            Merges the {groups.length} group{groups.length === 1 ? '' : 's'} shown
            {query ? ' for this search' : ''}, keeping the record marked “keep this” in each.
          </p>
          <button
            onClick={mergeAll}
            disabled={!!bulk}
            className="tl-btn tl-btn-primary tl-btn-sm disabled:opacity-50">
            <Merge className="w-4 h-4" />
            {bulk ? `Merging ${bulk.complete} of ${bulk.total}…` : `Merge all ${groups.length} shown`}
          </button>
        </div>
      )}

      {/* The whole backlog, not the page.

          "Merge all shown" is capped at what loaded — 200 groups — which is the
          right scale for pairs somebody has just looked at. It is the wrong
          scale for the 3,009 groups two overlapping enrols made in ninety
          seconds on 2026-09-11, where reviewing each one was never going to
          happen. This runs server-side and keeps going until nothing is left. */}
      {totalGroups > groups.length && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg px-3 py-3
                        bg-amber-50 border border-amber-200 dark:bg-amber-900/20 dark:border-amber-800">
          <div className="text-xs text-amber-900 dark:text-amber-200 max-w-xl">
            <b>{totalGroups.toLocaleString()} groups in total</b> — more than this page shows.
            Merging by phone keeps the <b>oldest</b> record in each, because the messages, calls
            and campaign rows written before the duplicate appeared already point at it.
            {sweepPlan && (
              <span className="block mt-1">
                Would fold <b>{sweepPlan.wouldMerge.toLocaleString()}</b> record
                {sweepPlan.wouldMerge === 1 ? '' : 's'} into their survivors. Nothing has changed yet.
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {!sweepPlan && (
              <button
                onClick={previewSweep}
                disabled={!!sweep}
                className="px-3 py-2 text-sm rounded-lg border border-amber-300 text-amber-900 hover:bg-amber-100
                           disabled:opacity-50 dark:border-amber-700 dark:text-amber-200 dark:hover:bg-amber-900/40">
                Check what this would merge
              </button>
            )}
            {sweepPlan && (
              <button
                onClick={runSweep}
                disabled={!!sweep}
                className="px-3 py-2 text-sm rounded-lg bg-amber-600 text-white hover:bg-amber-700
                           disabled:opacity-50 inline-flex items-center gap-1.5">
                <Merge className="w-4 h-4" />
                {sweep
                  ? `Merged ${sweep.merged.toLocaleString()} — ${sweep.remaining.toLocaleString()} left…`
                  : `Merge all ${totalGroups.toLocaleString()} by phone`}
              </button>
            )}
          </div>
        </div>
      )}

      {groups.length === 0 && (
        <p className="text-sm text-gray-400 py-10 text-center">
          No contacts share a number or an address. Nothing to clean up.
        </p>
      )}

      <div className="space-y-3">
        {groups.map((g, i) => {
          const key = groupKey(g, i);
          const survivorId = keepers[key] || g.contacts?.[0]?.id;
          return (
          <div key={key}
               className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
            <div className="px-4 py-2.5 border-b border-gray-100 dark:border-gray-700 flex items-center gap-2 text-sm">
              {g.matchedOn === 'phone'
                ? <Phone className="w-4 h-4 text-gray-400" />
                : <Mail className="w-4 h-4 text-gray-400" />}
              <span className="font-medium text-gray-800 dark:text-gray-100">{g.value}</span>
              <span className="text-xs text-gray-400">{g.contactCount} contacts</span>
              <div className="ml-auto flex items-center gap-2">
                {done[key] && (
                  <span className="text-xs text-green-600 dark:text-green-400 inline-flex items-center gap-1">
                    <Check className="w-3.5 h-3.5" /> {done[key]}
                  </span>
                )}
                {!done[key] && (
                  <button
                    onClick={() => mergeGroup(g, i)}
                    disabled={merging === key}
                    className="text-xs px-2.5 py-1 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 inline-flex items-center gap-1.5">
                    <Merge className="w-3.5 h-3.5" />
                    {merging === key ? 'Merging…' : 'Merge'}
                  </button>
                )}
              </div>
            </div>
            <div className="divide-y divide-gray-100 dark:divide-gray-700">
              {(g.contacts || []).map((c: any, idx: number) => (
                <label key={c.id}
                       className="px-4 py-2.5 flex items-center gap-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/40">
                  {/* Which record survives is the operator's call, not a policy. The
                      oldest is preselected because it is what other rows most likely
                      already point at, but the newer one is often the better-filled. */}
                  <input
                    type="radio"
                    name={key}
                    checked={survivorId === c.id}
                    onChange={() => setKeepers((k) => ({ ...k, [key]: c.id }))}
                    disabled={!!done[key]}
                  />
                  <div className="min-w-0 flex-1">
                    <button
                      onClick={(e) => { e.preventDefault(); navigate(`/user-directory/contacts/${c.id}`); }}
                      className="text-sm font-medium text-gray-900 dark:text-white hover:text-indigo-600 dark:hover:text-indigo-400 hover:underline text-left">
                      {c.name || '(unnamed)'}
                    </button>
                    <div className="text-xs text-gray-400 flex flex-wrap gap-x-3">
                      {c.company && <span>{c.company}</span>}
                      {c.source && <span>via {c.source}</span>}
                      {c.createdAt && <span>first seen {new Date(c.createdAt).toLocaleDateString()}</span>}
                    </div>
                  </div>
                  {idx === 0 && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-500 shrink-0">
                      oldest
                    </span>
                  )}
                  {survivorId === c.id && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 shrink-0">
                      keep this
                    </span>
                  )}
                </label>
              ))}
            </div>
          </div>
          );
        })}
      </div>
    </div>
  );
};

const Stat: React.FC<{ label: string; value: number; tone?: string }> = ({ label, value, tone }) => (
  <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
    <div className="text-xs uppercase tracking-wide text-gray-400">{label}</div>
    <div className={`text-2xl font-semibold mt-1 ${tone || 'text-gray-900 dark:text-white'}`}>
      {value.toLocaleString()}
    </div>
  </div>
);

export default ContactDuplicates;
